import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Upload, Save, RotateCcw, FileText, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface BackupRestoreProps {
  currentConfig: any;
  onRestore: (config: any) => void;
  onSave: () => void;
}

interface BackupData {
  version: string;
  timestamp: string;
  config: any;
  metadata: {
    name: string;
    description?: string;
    author?: string;
  };
}

const BackupRestore: React.FC<BackupRestoreProps> = ({ currentConfig, onRestore, onSave }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();

  // Gerar backup das configurações
  const generateBackup = (): BackupData => {
    return {
      version: '1.0.0',
      timestamp: new Date().toISOString(),
      config: currentConfig,
      metadata: {
        name: backupName || `Backup ${new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
        description: backupDescription,
        author: 'Admin'
      }
    };
  };

  // Exportar configurações
  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);

      const backup = generateBackup();
      const dataStr = JSON.stringify(backup, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tarot-theme-backup-${new Date().toISOString().split('T')[0]}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      // Limpar campos
      setBackupName('');
      setBackupDescription('');
      
    } catch (err) {
      setError('Erro ao exportar configurações');
    } finally {
      setIsExporting(false);
    }
  };

  // Importar configurações
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const backup: BackupData = JSON.parse(content);
        
        // Validar estrutura do backup
        if (!backup.config || !backup.version || !backup.timestamp) {
          throw new Error('Arquivo de backup inválido');
        }
        
        setPreviewConfig(backup);
        setShowImportPreview(true);
        
      } catch (err) {
        setError('Arquivo inválido. Selecione um backup válido.');
      } finally {
        setIsImporting(false);
      }
    };
    
    reader.readAsText(file);
    event.target.value = ''; // Limpar input
  };

  // Confirmar restauração
  const confirmRestore = () => {
    if (previewConfig) {
      onRestore(previewConfig.config);
      setShowImportPreview(false);
      setPreviewConfig(null);
    }
  };

  // Cancelar importação
  const cancelImport = () => {
    setShowImportPreview(false);
    setPreviewConfig(null);
  };

  return (
    <div className="space-y-6">
      {/* Seção de Exportação */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <div className="flex items-center space-x-3 mb-4">
          <Download className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Exportar Configurações</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nome do Backup
            </label>
            <input
              type="text"
              value={backupName}
              onChange={(e) => setBackupName(e.target.value)}
              placeholder="Ex: Tema Roxo Místico"
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Descrição (Opcional)
            </label>
            <textarea
              value={backupDescription}
              onChange={(e) => setBackupDescription(e.target.value)}
              placeholder="Descreva as características deste tema..."
              rows={3}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg transition-colors"
          >
            {isExporting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{isExporting ? 'Exportando...' : 'Baixar Backup'}</span>
          </button>
        </div>
      </div>

      {/* Seção de Importação */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <div className="flex items-center space-x-3 mb-4">
          <Upload className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-semibold text-white">Importar Configurações</h3>
        </div>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-300 mb-4">Selecione um arquivo de backup (.json)</p>
            
            <label className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              <span>{isImporting ? 'Carregando...' : 'Escolher Arquivo'}</span>
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                disabled={isImporting}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <div className="flex items-center space-x-3 mb-4">
          <Save className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Ações Rápidas</h3>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onSave}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>Salvar Atual</span>
          </button>
          
          <button
            onClick={() => {
              if (window.confirm('Tem certeza que deseja restaurar as configurações padrão?')) {
                // Implementar reset para configurações padrão
                const defaultConfig = {
                  colors: {
                    primary: '#8B5CF6',
                    secondary: '#F59E0B',
                    background: '#0F0F23',
                    surface: '#1A1A2E',
                    text: '#FFFFFF',
                    accent: '#FF6B6B'
                  },
                  typography: {
                    fontFamilyPrimary: "'Inter', sans-serif",
                    fontFamilyHeading: "'Playfair Display', serif",
                    fontSizeBase: '16px'
                  }
                };
                onRestore(defaultConfig);
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Restaurar Padrão</span>
          </button>
        </div>
      </div>

      {/* Modal de Preview de Importação */}
      {showImportPreview && previewConfig && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-900 rounded-lg p-6 max-w-md w-full border border-white/20"
          >
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-white">Confirmar Importação</h3>
            </div>
            
            <div className="space-y-3 mb-6">
              <div>
                <span className="text-sm text-gray-400">Nome:</span>
                <p className="text-white">{previewConfig.metadata.name}</p>
              </div>
              
              {previewConfig.metadata.description && (
                <div>
                  <span className="text-sm text-gray-400">Descrição:</span>
                  <p className="text-white text-sm">{previewConfig.metadata.description}</p>
                </div>
              )}
              
              <div>
                <span className="text-sm text-gray-400">Data:</span>
                <p className="text-white text-sm">
                  {new Date(previewConfig.timestamp).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-6">
              <p className="text-yellow-300 text-sm">
                ⚠️ Esta ação substituirá todas as configurações atuais. Certifique-se de fazer um backup antes de continuar.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelImport}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRestore}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Confirmar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Mensagem de Erro */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-4"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BackupRestore;